import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { getSupabase, hasStoredSession, isSupabaseLoaded, whenSupabaseLoaded } from "@/lib/supabase-lazy";
import { track } from "@/lib/analytics-client";
import { clearDraft } from "@/lib/draft-store";

/**
 * Loggar en inloggning i statistiken. Samma konto räknas som en ny inloggning
 * först efter 30 minuter, så att sidladdningar och tokenförnyelser inte
 * dubbelräknas. Kontots id skickas som etikett för att kunna räkna unika.
 */
function noteLogin(userId: string) {
  try {
    const key = `sellavo.lastlogin.${userId}`;
    const last = Number(localStorage.getItem(key) ?? 0);
    if (Date.now() - last < 30 * 60_000) return;
    localStorage.setItem(key, String(Date.now()));
  } catch {
    /* blockerad lagring – logga ändå */
  }
  track("login", { label: userId });
}


type AuthContextValue = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue>({
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsub: (() => void) | undefined;
    let stopped = false;

    // Utan sparad inloggning behövs inte databasbiblioteket vid sidstart – det
    // hämtas först när någon loggar in eller när en annan del av sidan behöver det.
    const attach = (supabase: Awaited<ReturnType<typeof getSupabase>>) => {
      if (stopped) return;
      const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
        setSession(s);
        setLoading(false);
        if (event === "SIGNED_IN" && s?.user) noteLogin(s.user.id);
        // Utkastet är personligt – det får aldrig ligga kvar åt nästa besökare.
        if (event === "SIGNED_OUT") void clearDraft();
      });
      unsub = () => sub.subscription.unsubscribe();

      void supabase.auth.getSession().then(({ data }) => {
        setSession(data.session);
        setLoading(false);
      });
    };

    if (hasStoredSession()) {
      void getSupabase().then(attach);
    } else {
      setLoading(false);
      whenSupabaseLoaded(attach);
    }

    // Synka inloggning mellan flikar (t.ex. när bekräftelselänken öppnas i en ny flik)
    const refresh = async (incoming?: Session | null) => {
      // Ingen inloggning i webbläsaren och inget bibliotek laddat = inget att synka.
      if (!incoming && !isSupabaseLoaded() && !hasStoredSession()) return;
      try {
        const supabase = await getSupabase();
        if (incoming) {
          const { data, error } = await supabase.auth.setSession({
            access_token: incoming.access_token,
            refresh_token: incoming.refresh_token,
          });
          if (!error) setSession(data.session);
          return;
        }
        const { data } = await supabase.auth.getSession();
        setSession(data.session);
      } catch {
        // En tillfällig flik-/token-race får inte lämna registreringsrutan fast.
        window.setTimeout(() => void refresh(incoming), 750);
      }
    };
    const onStorage = (e: StorageEvent) => {
      if (!e.key || e.key.startsWith("sb-") || e.key === "sellavo:auth-ping") refresh();
    };
    const onFocus = () => refresh();
    const onVisibility = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel("sellavo:auth");
      channel.onmessage = (event: MessageEvent<{ type?: string; session?: Session }>) => {
        if (event.data?.type !== "signed-in") return;
        void refresh(event.data.session ?? null);
        // Säg till bekräftelsefliken att vi tagit över, så den kan be om att stängas.
        if (!window.location.pathname.startsWith("/bekraftad")) {
          try {
            const ack = new BroadcastChannel("sellavo:auth");
            ack.postMessage({ type: "resumed" });
            ack.close();
          } catch {
            // ignore
          }
        }
      };
    } catch {
      // ignore
    }


    return () => {
      stopped = true;
      unsub?.();
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      channel?.close();
    };
  }, []);

  const value: AuthContextValue = {
    user: session?.user ?? null,
    session,
    loading,
    signOut: async () => {
      await clearDraft();
      // Notistoken hör till kontot – den ska bort när man loggar ut, annars
      // kan nästa användare av telefonen få notiser som inte är hens.
      await releasePushOnSignOut();
      const supabase = await getSupabase();
      await supabase.auth.signOut();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Tar bort enhetens pushprenumeration innan utloggning. Tyst om det misslyckas. */
async function releasePushOnSignOut() {
  try {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    const reg = await navigator.serviceWorker.getRegistration();
    const subscription = await reg?.pushManager?.getSubscription();
    if (!subscription) return;
    const { removePushSubscription } = await import("@/lib/push.functions");
    await removePushSubscription({ data: { endpoint: subscription.endpoint } });
    await subscription.unsubscribe();
  } catch {
    /* utloggningen ska aldrig fastna på detta */
  }
  try {
    const { setNativeBadge } = await import("@/app-bridge/core/wrapper");
    await setNativeBadge(0);
  } catch {
    /* ignoreras */
  }
}

export function useAuth() {
  return useContext(AuthContext);
}
